/*
 * Bento media gallery (sections/bento-media-gallery.liquid). Loaded by that section only.
 *
 * Masonry: every tile gets a grid row span from its width and its aspect ratio (data-ratio), so each image and video
 * keeps its own shape at every screen size. Until this runs the tiles are plain CSS aspect-ratio boxes.
 *
 * Lightbox: PhotoSwipe 5, imported from the asset URLs the section passes in (data-lightbox-src / data-core-src),
 * so it never loads on other pages; the core is only fetched when a tile is first opened. Videos open as HTML slides
 * built from the tile's <template> and play only while their slide is the current one.
 */
if (!customElements.get('bento-gallery')) {
  const ROW_HEIGHT = 4; // px, keep in sync with --bento-row in section-bento-media-gallery.css

  class BentoGallery extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.bento-grid');
      this.items = Array.from(this.querySelectorAll('.bento-item'));
      if (!this.grid || !this.items.length) return;

      this.gridWidth = 0;
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this.grid);
      this.layout();
      this.initLightbox();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.lightbox) this.lightbox.destroy();
      this.lightbox = null;
    }

    layout() {
      const width = this.grid.clientWidth;
      // Skip while hidden, and when only the height changed (which this layout itself causes)
      if (!width || width === this.gridWidth) return;
      this.gridWidth = width;

      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      // Read every width before writing any span, so the browser only lays the grid out once
      const widths = this.items.map((item) => item.getBoundingClientRect().width);
      this.items.forEach((item, index) => {
        const ratio = parseFloat(item.dataset.ratio) || 1;
        const span = Math.ceil((widths[index] / ratio + gap) / ROW_HEIGHT);
        item.style.gridRowEnd = `span ${Math.max(span, 1)}`;
      });
      this.setAttribute('data-ready', '');
    }

    initLightbox() {
      const { lightboxSrc, coreSrc } = this.dataset;
      if (!lightboxSrc || !coreSrc || !this.querySelector('a[data-pswp]')) return;

      import(lightboxSrc)
        .then(({ default: PhotoSwipeLightbox }) => {
          if (!this.isConnected) return;

          const lightbox = new PhotoSwipeLightbox({
            gallery: this,
            children: 'a[data-pswp]',
            pswpModule: () => import(coreSrc),
            bgOpacity: 0.94,
            closeTitle: this.dataset.closeTitle,
            arrowPrevTitle: this.dataset.prevTitle,
            arrowNextTitle: this.dataset.nextTitle,
            // Videos keep clear of the close button and (on desktop) the arrows; images use the whole screen
            paddingFn: (viewportSize, itemData) => {
              if (itemData.type !== 'html') return { top: 0, bottom: 0, left: 0, right: 0 };
              const side = viewportSize.x > 749 ? 72 : 0;
              return { top: 56, bottom: 24, left: side, right: side };
            },
          });

          // Video tiles (data-pswp-type="html"): the slide is the markup in the tile's <template>
          lightbox.addFilter('domItemData', (itemData, element, linkEl) => {
            if (itemData.type === 'html') {
              const template = linkEl && linkEl.parentElement.querySelector('template.bento-item__lightbox');
              itemData.html = template ? template.innerHTML : '';
            }
            return itemData;
          });

          lightbox.on('contentActivate', ({ content }) => BentoGallery.toggleMedia(content.element, true));
          lightbox.on('contentDeactivate', ({ content }) => BentoGallery.toggleMedia(content.element, false));

          // A press on a video's control bar works the controls instead of dragging the slide
          lightbox.on('pointerDown', (event) => {
            const { target, clientY } = event.originalEvent;
            if (!(target instanceof HTMLVideoElement)) return;
            const rect = target.getBoundingClientRect();
            if (clientY > rect.bottom - Math.max(64, rect.height * 0.2)) event.preventDefault();
          });

          lightbox.init();
          this.lightbox = lightbox;
        })
        .catch(() => {
          // Without the lightbox the tiles still link to the full image or the video
        });
    }

    // Plays the current slide's video (or loads its YouTube/Vimeo player) and stops the one being left
    static toggleMedia(element, active) {
      if (!element) return;

      const video = element.querySelector('video');
      if (video) {
        if (active) {
          const playing = video.play();
          if (playing) playing.catch(() => {}); // autoplay blocked: the controls are there
        } else {
          video.pause();
        }
      }

      const iframe = element.querySelector('iframe[data-src]');
      if (iframe) iframe.src = active ? iframe.dataset.src : 'about:blank';
    }
  }

  customElements.define('bento-gallery', BentoGallery);
}
